import type { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import type { PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { useTranslationTyped } from 'i18n/i18n'
import {
  ScheduledDayOfWeekAtTime,
  ScheduledDayOfMonthOnYear,
} from 'pages/student/assignment/ScheduledTimeParts'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { BreakoutButton } from './design-system/BreakoutButton'
import React from 'react'

type RoomCardContext = 'instructor' | 'student'

export const RoomCard = observer(function RoomCard({
  room,
  roomStateStatus,
  context,
  virtualName,
  bottomContent,
  onClick,
  hideCTA,
}: {
  room: RoomState
  roomStateStatus: RoomStateStatus
  context: RoomCardContext
  virtualName: string
  bottomContent?: React.ReactNode
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  hideCTA?: boolean
}) {
  return (
    <BaseRoomCard
      className={classNames('m-0.5', {
        'hover:outline hover:outline-2 hover:outline-breakout-yellow':
          !!onClick,
      })}
      title={room.data.roomStateName ?? virtualName}
      context={context}
      titleOverrideOrRoomStateStatus={{
        status: roomStateStatus,
        titleOverride: undefined,
      }}
      scheduledAt={room.data.scheduledAt ?? undefined}
      users={room.usersSortedByName}
      bottomContent={bottomContent}
      onClick={onClick}
      hideCTA={hideCTA}
    />
  )
})

const BaseRoomCard = observer(function BaseRoomCard({
  className,
  context,
  title,
  hideCTA,
  subtitle,
  scheduledAt,
  isUnassignedRoom,
  users,
  bottomContent,
  titleOverrideOrRoomStateStatus,
  onClick,
  testId = 'room-card',
}: {
  className?: string
  context: RoomCardContext
  title?: string
  subtitle?: string
  scheduledAt?: Date
  users: PublicUser[]
  bottomContent?: React.ReactNode
  // we need one of these to be defined to drive the top of the card
  titleOverrideOrRoomStateStatus:
    | { status: RoomStateStatus; titleOverride: undefined }
    | { status: undefined; titleOverride: React.ReactNode }
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  isUnassignedRoom?: boolean
  hideCTA?: boolean
  testId?: string
}) {
  const { tt } = useTranslationTyped()
  const hasUsers = users.length > 0

  // max display in card
  const MAX_USERS = 8

  // minus one to make space for the "show more" count
  const displayUsers =
    users.length > MAX_USERS ? users.slice(0, MAX_USERS - 1) : users

  const remainingCount = users.length - displayUsers.length

  const showMoreCount = remainingCount > 0

  const { titleOverride, status: roomStateStatus } =
    titleOverrideOrRoomStateStatus

  const scheduledDateInvalid =
    roomStateStatus !== undefined &&
    [
      RoomStateStatus.mustScheduleSuspended,
      RoomStateStatus.mustSchedule,
    ].includes(roomStateStatus)

  return (
    <div
      data-testid={testId}
      role="group"
      aria-label={title}
      className={classNames(
        'group/card relative flex flex-1 flex-col gap-2 rounded-[20px] bg-surface p-3',
        className,
        context,
        {
          'hover:bg-surface-hover cursor-pointer': onClick !== undefined,
        }
      )}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onClick) onClick()
      }}
    >
      {titleOverride}
      {!titleOverride && (
        <div className="flex flex-col">
          <h2 className="h-[18px] self-stretch text-nowrap text-title-small">
            <ScheduledDayOfWeekAtTime
              scheduledAt={scheduledDateInvalid ? undefined : scheduledAt}
              tt={tt}
              placeholder={tt.student_assignment.join_dialog_v2.not_scheduled()}
            />
          </h2>
          <strong className="h-[15px] text-body-small text-on-surface-var">
            <ScheduledDayOfMonthOnYear
              scheduledAt={scheduledDateInvalid ? undefined : scheduledAt}
              placeholder="-"
            />
          </strong>
          <strong
            className="h-[15px] truncate text-label-small"
            data-testid="room-card-name"
          >
            {title ?? ''}
            {subtitle && ` - ${subtitle}`}
          </strong>
        </div>
      )}

      <div className="min-h-[132px]">
        {hasUsers ? (
          <div className="flex flex-wrap gap-x-1">
            {displayUsers.map((user, i) => (
              <div
                key={i}
                style={{ width: `calc(50% - 2px)` }}
                className="my-0.5 flex h-[29px] flex-row items-center gap-1 text-body-medium"
              >
                <BreakoutUserAvatar
                  className="min-h-6 min-w-6"
                  user={user}
                  size="small"
                />
                <div
                  style={{ width: `calc(100% - 28px)` }}
                  className="flex flex-col gap-[-4px]"
                >
                  <strong
                    className="truncate text-body-medium"
                    data-testid="room-card-first-name"
                  >
                    {user.data.firstName}
                  </strong>
                  <strong className="truncate text-body-small text-on-surface-var">
                    {user.data.lastName}
                  </strong>
                </div>
              </div>
            ))}
            {showMoreCount && (
              <div
                style={{ width: `calc(50% - 2px)` }}
                className="my-0.5 flex min-h-[29px] flex-row items-center gap-1 text-body-medium"
              >
                <div className="text-fixed-on-accent-color flex min-h-6 min-w-6 items-center justify-center rounded-full bg-white text-[10px] font-medium">
                  +{remainingCount}
                </div>
                <div
                  style={{ width: `calc(100% - 28px)` }}
                  className="flex flex-col"
                >
                  <strong className="truncate text-body-medium">
                    {tt.instructor_assignment.groups.more_students()}
                  </strong>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-center text-body-medium text-on-surface-var">
            <span>{tt.student_assignment.join_dialog_v2.empty_group()}</span>
          </div>
        )}
      </div>
      {(context === 'student' || hideCTA) && (
        <div className="mx-auto mt-auto min-h-[33px] w-full">
          {bottomContent}
        </div>
      )}
      {context === 'instructor' && !hideCTA && (
        <>
          {/* This div simulates bottomContent - it's necessary to achieve correct height */}
          <div className="min-h-[33px]"></div>
          <div className="absolute bottom-3 left-0 h-[33px] w-full overflow-hidden rounded-b-[20px] group-hover/card:visible">
            <div className="duration-[250ms] flex h-[33px] transform flex-col items-center justify-center transition ease-out group-hover/card:translate-y-[-33px] group-hover/card:opacity-0 group-focus/card:translate-y-[-33px] group-focus/card:opacity-0">
              {bottomContent}
            </div>
            <div className="h-[33px] translate-y-[0] transform px-3 transition duration-200 ease-out group-hover/card:translate-y-[-33px] group-focus/card:translate-y-[-33px]">
              {!isUnassignedRoom && (
                <BreakoutButton
                  kind="accent"
                  size="small"
                  fullWidth
                  tabIndex={-1}
                >
                  {tt.instructor_assignment.groups.manage_group()}
                </BreakoutButton>
              )}
              {isUnassignedRoom && (
                <BreakoutButton
                  kind="error"
                  size="small"
                  fullWidth
                  tabIndex={-1}
                >
                  {tt.instructor_assignment.groups.manage_unassigned()}
                </BreakoutButton>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
})

export function UnassignedStudentsCard({
  users,
  bottomContent,
  onClick,
  hideCTA,
}: {
  users: PublicUser[]
  bottomContent?: React.ReactNode
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  hideCTA?: boolean
}) {
  const { t, tt } = useTranslationTyped()

  return (
    <BaseRoomCard
      testId="unassigned-students-card"
      context="instructor"
      className={classNames('border-2 border-core-error')}
      isUnassignedRoom
      users={users}
      onClick={onClick}
      bottomContent={bottomContent}
      hideCTA={hideCTA}
      titleOverrideOrRoomStateStatus={{
        status: undefined,
        titleOverride: (
          <div className="flex min-h-12 items-center gap-2">
            <TriangleAlertIcon size={24} className="shrink-0 text-core-error" />
            <div className="flex flex-col">
              <h2 className="text-title-small">
                {tt.instructor_assignment.groups.unassigned_students()}
              </h2>
              {/* t instead of tt to take advantage of i18n pluralization */}
              <strong className="h-[15px] text-body-small text-on-surface-var">
                {t('instructor_assignment.groups.unassigned_students_count', {
                  count: users.length,
                })}
              </strong>
            </div>
          </div>
        ),
      }}
    />
  )
}
